<template>
  <section class="section">
    <div v-if="!allowed" class="not-allowed">{{ $t('prm_not_available') }}</div>

    <div v-else class="prm-page">

      <b-tabs v-model="activeTab" class="prm-tabs">
        <b-tab-item>
          <template slot="header">
            <b-icon pack="fas" icon="table-cells-large" size="is-small"></b-icon>
            <span>{{ $t('prm_tab_portfolio') }}</span>
          </template>
          <prm-portfolio-tab v-if="company && portfolioMounted" :company="company"
            :current-project-id="project ? project.id : null" @open="openProject"></prm-portfolio-tab>
        </b-tab-item>

        <b-tab-item>
          <template slot="header">
            <b-icon pack="fas" icon="gauge-high" size="is-small"></b-icon>
            <span>{{ $t('prm_tab_dashboard') }}</span>
          </template>
          <prm-dashboard-tab v-if="project" :project="project" :prm-version="prmVersion"></prm-dashboard-tab>
        </b-tab-item>

        <b-tab-item>
          <template slot="header">
            <b-icon pack="fas" icon="file-lines" size="is-small"></b-icon>
            <span>{{ $t('prm_tab_template') }}</span>
          </template>
          <prm-template-tab v-if="project" :project="project" @imported="prmVersion++"></prm-template-tab>
        </b-tab-item>

        <b-tab-item>
          <template slot="header">
            <b-icon pack="fas" icon="clipboard-list" size="is-small"></b-icon>
            <span>{{ $t('prm_tab_summary') }}</span>
          </template>
          <prm-summary-tab v-if="project" :project="project" :prm-version="prmVersion"></prm-summary-tab>
        </b-tab-item>

        <!-- Configurador de alertas: lo dejamos puesto pero bloqueado; lo implementamos más adelante. -->
        <b-tab-item :disabled="true">
          <template slot="header">
            <b-icon pack="fas" icon="bell" size="is-small"></b-icon>
            <span>{{ $t('prm_tab_alerts') }}</span>
          </template>
        </b-tab-item>
      </b-tabs>
    </div>
  </section>
</template>

<script>
  import { mapGetters } from 'vuex'
  import PrmTemplateTab from '·/components/dashboard-prm/template/template-tab.vue'
  import PrmSummaryTab from '·/components/dashboard-prm/summary/summary-tab.vue'
  import PrmDashboardTab from '·/components/dashboard-prm/dashboard/dashboard-tab.vue'
  import PrmPortfolioTab from '·/components/dashboard-prm/portfolio/portfolio-tab.vue'
  import { getRate } from '·/components/dashboard-prm/shared/currency'

  export default {
    middleware: 'auth',
    title: 'prm_dashboard_title',
    subtitle: 'prm_dashboard_subtitle',

    components: { PrmTemplateTab, PrmSummaryTab, PrmDashboardTab, PrmPortfolioTab },

    // Abrimos en Plantilla PRM; el Portfolio (índice 0) se monta al visitarlo (carga cruzada de empresa).
    data: () => ({
      activeTab: 2,
      prmVersion: 0, // sube en cada import del PRM → el Resumen recarga sus datos derivados
      portfolioMounted: false, // el tab Portfolio se monta la primera vez que se abre (evita su carga si no se usa)
    }),

    computed: {
      ...mapGetters({ project: 'app/project', company: 'app/company' }),

      allowed () {
        return this.company && (this.company.alias === 'tkelevator' || this.company.alias === 'trial')
      },
    },

    watch: {
      // Al abrir el Dashboard o el Resumen refrescamos sus datos derivados del PRM (pueden haber
      // cambiado en Plantilla). El Portfolio se monta la primera vez que se visita.
      activeTab (index) {
        if (index === 0) this.portfolioMounted = true
        if (index === 1 || index === 3) this.prmVersion++
      },
    },

    methods: {
      // Abre el Dashboard PRM de otro proyecto desde el Portfolio: fija el proyecto activo en el
      // store (mismo flujo que el selector de proyectos) y salta a su tab Dashboard.
      async openProject (project) {
        await this.$store.dispatch('app/setProject', { project })
        await this.$store.dispatch('app/setTags', { tags: project.tags })
        if (project.configs) {
          await this.$store.dispatch('app/setVisualConfig', { config: project.configs })
        }
        await this.$store.dispatch('app/setDashboardReport', { dashboardReport: {} })
        this.activeTab = 1   // abrimos el cockpit del proyecto seleccionado
        this.prmVersion++    // recargamos los datos derivados del nuevo proyecto
      },
    },

    // Precargamos la tasa de cambio al entrar al dashboard (solo llama si ≥1h de la última),
    // así el tab Resumen · Hitos de pago ya la tiene lista sin llamar al abrirse.
    mounted () {
      if (this.allowed) {
        getRate('USD', 'EUR')
      }
    },
  }
</script>

<style lang="scss" scoped>
  // Anclamos la barra de pestañas SIN salto: quitamos el padding superior de la sección y
  // cancelamos con margen negativo el padding del wrapper .p-4 del layout (16px) + su borde
  // superior (1px), para que el tab arranque exactamente en su posición fija (161px = navbar
  // 80px + título de página sticky 81px). Así no hay desplazamiento antes de fijarse.
  .section { padding-top: 0; }
  .prm-page { min-width: 0; margin-top: -17px; }
  .not-allowed { padding: 40px; text-align: center; color: #9aa4b2; }

  // Pestañas principales al estilo de la página: subrayado limpio, azul en la activa.
  .prm-tabs ::v-deep {
    // Barra de pestañas fija al hacer scroll: se ancla justo bajo el título de página sticky
    // (161px). Fondo blanco para que el contenido pase por debajo. Sin salto (ver .prm-page).
    .tabs {
      position: sticky; top: 161px; z-index: 8;
      margin-bottom: 14px; background: #fff; padding-top: 4px;
    }
    .tabs ul { border-bottom: 1px solid #e2e7ee; }
    .tabs li a {
      color: #6b7787; font-size: 13px; font-weight: 600; padding: 8px 14px;
      border-bottom-width: 2px; border-bottom-color: transparent; gap: 6px;
    }
    .tabs li a:hover { color: #1e3046; background: transparent; border-bottom-color: #c3ccd8; }
    .tabs li.is-active a { color: #2b5cad; border-bottom-color: #2b5cad; background: transparent; }
    .tabs li.is-disabled a { color: #b8c0cc; cursor: not-allowed; }
    .tab-content { padding: 0; }
  }
</style>
